import { Divider, Paper, Stack, useTheme } from '@mui/material';
import { alpha, Box } from '@mui/system';
import React from 'react'
import { IsNotEmpty } from '../Helpers';
import useViewportMode from '../hooks/useViewportMode';
import ConditionalDisplay from './ConditionalDisplay';

export interface PanelSegmentProps {
    titleContent?: string | JSX.Element | (() => JSX.Element)
    toolbar?: JSX.Element | (() => JSX.Element)
    menu?: JSX.Element | (() => JSX.Element)
    fillHeight?: boolean;
    boundedHeight?: boolean;
    children?: React.ReactElement | React.ReactChild[] | (() => React.ReactChild);
}

const PanelSegment = ({ titleContent = '', toolbar = null, menu = null, fillHeight = false, boundedHeight = false, children = () => null }: PanelSegmentProps) => {
    const theme = useTheme();
    const { isDesktop } = useViewportMode();
    const fetcher = children instanceof Function ? (children as Function) : (() => children);
    const panelHeaderColor = alpha(
        theme.palette.background.neutral ?? theme.palette.background.paper,
        theme.palette.mode === 'light' ? 0.18 : 0.16
    );

    return (
        <Paper sx={{
            border: 'solid 1px',
            borderColor: theme.palette.divider,
            borderRadius: { xs: 0, sm: 2 },
            ...(fillHeight && !boundedHeight && { minHeight: { xs: 'auto', lg: 'calc(100vh - 80px)' } }),
            ...(boundedHeight && {
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }),
        }}>
            {isDesktop ? (
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    borderRadius: '5px 5px 0px 0px',
                    padding: theme.spacing(2),
                    backgroundColor: panelHeaderColor
                }} data-testid="desktop-panel-header">
                    <Box flexGrow={1}>{titleContent}</Box>
                    <Box>{toolbar}</Box>
                </Box>
            ) : (
                <Stack
                    data-testid="compact-panel-header"
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{
                        width: '100%',
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.5, sm: 2 },
                        backgroundColor: panelHeaderColor,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>{titleContent}</Box>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            '& > *': { maxWidth: '100%' },
                            '& .MuiButtonGroup-root': { flexWrap: 'wrap' },
                        }}
                    >
                        {toolbar}
                    </Box>
                </Stack>
            )}
            <ConditionalDisplay ifTrue={IsNotEmpty(menu)}>
                {() => <Box>{menu}</Box>}
            </ConditionalDisplay>
            <Divider />
            {boundedHeight ? <Box
                data-testid="bounded-panel-content"
                sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                {fetcher()}
            </Box> : fetcher()}
        </Paper>
    )
}

export default PanelSegment;
